import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pricingApi, type PriceBook } from '../../api/pricing';

export function PriceBookManagerPage() {
  const [books, setBooks] = useState<PriceBook[]>([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const reload = () => void pricingApi.listPriceBooks().then(setBooks).catch((e) => setError(String(e)));
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!code.trim() || !name.trim()) return;
    try {
      await pricingApi.createPriceBook({ code: code.trim(), name: name.trim(), currency: 'INR' });
      setCode('');
      setName('');
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Price Book Manager</h1>
      <div className="mt-4 flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left text-slate-600">
          <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Currency</th><th className="px-4 py-3">Status</th></tr>
        </thead>
        <tbody>
          {books.map((b) => (
            <tr key={b.id} className="border-b">
              <td className="px-4 py-3 font-medium">{b.name}</td>
              <td className="px-4 py-3">{b.code}</td>
              <td className="px-4 py-3">{b.currency}</td>
              <td className="px-4 py-3">{b.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BulkPriceUpdatePage() {
  const [books, setBooks] = useState<PriceBook[]>([]);
  const [bookId, setBookId] = useState('');
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    void pricingApi.listPriceBooks().then((b) => { setBooks(b); if (b[0]?.id) setBookId(b[0].id); });
  }, []);

  const run = async () => {
    if (!bookId) return;
    const items = csv.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
      const [product_id, base_price] = line.split(',');
      return { product_id: product_id.trim(), base_price: parseFloat(base_price) };
    }).filter((i) => i.product_id && !Number.isNaN(i.base_price));
    try {
      const r = await pricingApi.bulkUpdatePrices({ price_book_id: bookId, items, reason: 'bulk import' });
      setResult(`Updated ${r.updated ?? items.length} prices`);
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Bulk Price Update</h1>
      <select value={bookId} onChange={(e) => setBookId(e.target.value)} className="mt-4 rounded-lg border px-3 py-2 text-sm">
        {books.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <textarea value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="product_id,base_price (one per line)" className="mt-4 w-full rounded-lg border px-3 py-2 text-sm font-mono" rows={8} />
      <button type="button" onClick={() => void run()} className="mt-2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Bulk update</button>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {result && <p className="mt-4 text-sm text-emerald-700">{result}</p>}
    </div>
  );
}

export function MarketplacePricingPage() {
  const [items, setItems] = useState<{ id: string; product_id: string; marketplace: string; list_price: number; sale_price?: number }[]>([]);
  const [productId, setProductId] = useState('');
  const [marketplace, setMarketplace] = useState('amazon');
  const [listPrice, setListPrice] = useState('');
  const [error, setError] = useState('');

  const reload = () => void pricingApi.listMarketplacePrices(productId ? { product_id: productId } : undefined).then(setItems).catch((e) => setError(String(e)));
  useEffect(() => { reload(); }, [productId]);

  const save = async () => {
    if (!productId.trim() || !listPrice) return;
    try {
      await pricingApi.upsertMarketplacePrice({ product_id: productId.trim(), marketplace, list_price: parseFloat(listPrice) });
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Marketplace Pricing</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Product ID filter" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={listPrice} onChange={(e) => setListPrice(e.target.value)} placeholder="List price" className="rounded-lg border px-3 py-2 text-sm" />
        <select value={marketplace} onChange={(e) => setMarketplace(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
          <option value="amazon">Amazon</option>
          <option value="flipkart">Flipkart</option>
          <option value="myntra">Myntra</option>
          <option value="shopify">Shopify</option>
        </select>
        <button type="button" onClick={() => void save()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Save price</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left"><tr><th className="px-4 py-3">Product</th><th className="px-4 py-3">Marketplace</th><th className="px-4 py-3">List</th><th className="px-4 py-3">Sale</th></tr></thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} className="border-b">
              <td className="px-4 py-3">{m.product_id}</td>
              <td className="px-4 py-3">{m.marketplace}</td>
              <td className="px-4 py-3">{m.list_price}</td>
              <td className="px-4 py-3">{m.sale_price ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
